import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, X, CheckCheck } from "lucide-react";
import { format, parseISO } from "date-fns";

function formatDate(d) {
  try { return format(parseISO(d), "EEE, dd MMM yyyy"); } catch { return d; }
}

export default function AlertsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get("/notifications");
      setNotifications(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dismiss = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      window.dispatchEvent(new Event("admin-data-updated"));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      window.dispatchEvent(new Event("admin-data-updated"));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const unread = notifications.filter((n) => !n.read);
  const read = notifications.filter((n) => n.read);

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="alerts-page">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Action Needed</div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#2A2421] flex items-center gap-4">
          <AlertTriangle size={36} strokeWidth={1.5} className="text-red-700" /> Reassignment Alerts
        </h1>
        <p className="text-[#5C5652] mt-4 max-w-xl">
          Team members who declined an assignment. Reassign someone new so no event is left short.
        </p>
      </div>

      {loading ? (
        <div className="text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>
      ) : unread.length === 0 && read.length === 0 ? (
        <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]" data-testid="no-alerts">
          No reassignment alerts. Everyone's on board.
        </div>
      ) : (
        <>
          {unread.length > 0 && (
            <div className="space-y-3 mb-12">
              {unread.map((n) => (
                <div key={n.id} className="p-5 bg-white border-l-2 border-red-600 border border-[#EAE7E0] flex items-center gap-4" data-testid={`alert-${n.id}`}>
                  <div className="flex-1">
                    <div className="text-sm text-[#2A2421]">{n.message}</div>
                    <div className="text-xs text-[#5C5652] mt-1">{formatDate(n.event_date)} · {n.couple_names || n.event_title}</div>
                  </div>
                  <Link to={`/events/${n.event_id}`} className="text-[10px] uppercase tracking-[0.25em] text-[#C88270] hover:text-[#B37363]" data-testid={`alert-open-${n.id}`}>Reassign →</Link>
                  <button onClick={() => markRead(n.id)} title="Mark resolved" data-testid={`alert-read-${n.id}`} className="text-[#829384] hover:text-[#6d7c6f]"><CheckCheck size={16} /></button>
                  <button onClick={() => dismiss(n.id)} title="Dismiss" data-testid={`alert-dismiss-${n.id}`} className="text-[#5C5652] hover:text-red-700"><X size={16} /></button>
                </div>
              ))}
            </div>
          )}

          {read.length > 0 && (
            <div data-testid="resolved-alerts">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-4">Resolved</div>
              <div className="bg-white border border-[#EAE7E0]">
                {read.map((n) => (
                  <div key={n.id} className="flex items-center gap-4 p-4 border-b border-[#EAE7E0] last:border-0 opacity-70" data-testid={`resolved-alert-${n.id}`}>
                    <div className="flex-1">
                      <div className="text-sm text-[#2A2421] line-through">{n.message}</div>
                      <div className="text-xs text-[#5C5652] mt-1">{formatDate(n.event_date)} · {n.couple_names || n.event_title}</div>
                    </div>
                    <button onClick={() => dismiss(n.id)} title="Delete" data-testid={`resolved-dismiss-${n.id}`} className="text-[#5C5652] hover:text-red-700"><X size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
